import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { UsersRound, Search, Loader2, GraduationCap, CheckCircle, AlertTriangle } from 'lucide-react';
import { personnelApi, trainingApi } from '../lib/api';

export default function EnrolledPersonnelPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [personnel, setPersonnel] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPersonnel();
  }, []);

  const loadPersonnel = async () => {
    try {
      const response = await personnelApi.getAll();
      setPersonnel(response.data || []);
    } catch (error) {
      toast.error('Errore nel caricamento del personale');
    } finally {
      setLoading(false);
    }
  };

  const filteredPersonnel = personnel.filter(p => {
    const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const trainedCount = personnel.filter(p => p.has_training).length;
  const notTrainedCount = personnel.filter(p => !p.has_training).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <UsersRound className="w-6 h-6" />
          Personale Iscritto
        </h1>
        <p className="text-muted-foreground">Gestione iscrizioni ai corsi di formazione</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totale Personale</p>
                <p className="text-3xl font-bold">{personnel.length}</p>
              </div>
              <UsersRound className="w-10 h-10 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Con Formazione</p>
                <p className="text-3xl font-bold text-green-600">{trainedCount}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Senza Formazione</p>
                <p className="text-3xl font-bold text-orange-600">{notTrainedCount}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Cerca personale..."
            className="pl-9"
          />
        </div>
      </div>

      {/* Personnel List */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Personale</CardTitle>
          <CardDescription>
            Personale iscritto ai corsi di formazione
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredPersonnel.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <UsersRound className="w-12 h-12 mx-auto mb-4" />
              <p>Nessun personale trovato</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredPersonnel.map((person) => (
                <div key={person.id} className="flex items-center justify-between p-3 rounded border">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="font-semibold text-primary">
                        {person.first_name?.[0]}{person.last_name?.[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{person.first_name} {person.last_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {person.job_title || 'Ruolo non specificato'} • {person.pdv_name || 'UL non assegnata'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {person.has_training ? (
                      <Badge variant="default" className="bg-green-500">
                        <GraduationCap className="w-3 h-3 mr-1" />
                        Formazione OK
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-orange-500 text-orange-500">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Da formare
                      </Badge>
                    )}
                    <Button variant="outline" size="sm">
                      Iscrivi a Corso
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
